import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
	ProgramOwnerCheckoutDisclosuresDto,
	ServicesProvider,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to update checkout disclosures configuration for a services provider
 */
export const updateCheckoutDisclosures = async (
	servicesProviderId: string,
	data: ProgramOwnerCheckoutDisclosuresDto,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.checkoutDisclosures(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for updating checkout disclosures configuration for a services provider
 */
export const useUpdateCheckoutDisclosures = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: ProgramOwnerCheckoutDisclosuresDto) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to update checkout disclosures without a services provider ID.',
				);
			}
			return updateCheckoutDisclosures(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(queries.servicesProviders.detail(data.id).queryKey, data);
		},
	});
};
